import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Styles from './BoardMembers100.module.scss';
import { submitToZoho, submitPartialBeacon } from '../../service/zohoFormService';
import {
  applicationSnapshotToFields,
  briefingSnapshotToFields,
  flushPendingPartials,
  submitFinalDraft,
  submitPartialDraft,
} from '../../service/formPartialService';
import { trackGoogleAdsSubmitLeadFormConversion } from '../../utils/analytics';

/** Reliable read from a live form (name / radio / textarea). */
function getFieldValue(form, name) {
  if (!form?.elements) return '';
  const el = form.elements.namedItem(name);
  if (!el) return '';
  if (el.length != null && el[0] && el[0].type === 'radio') {
    for (let i = 0; i < el.length; i += 1) {
      if (el[i].checked) return el[i].value;
    }
    return '';
  }
  if (el.type === 'radio') return el.checked ? el.value : '';
  return String(el.value ?? '').trim();
}

function formHasAnyValue(data) {
  return Object.values(data).some((v) => v && String(v).trim());
}

async function syncPartialToApi(formType, snapshot, submittedRef) {
  if (submittedRef.current || !formHasAnyValue(snapshot)) return;
  const fields =
    formType === 'application'
      ? applicationSnapshotToFields(snapshot)
      : briefingSnapshotToFields(snapshot);
  if (!Object.keys(fields).length) return;
  try {
    await submitPartialDraft({ formType, fields, useBeacon: false });
  } catch (_) {
    /* ignore */
  }
}

/** Read current field values from the DOM (needed on close: native `change` often fires on blur, so refs can be stale). */
function snapshotApplicationForm(form) {
  if (!form) return {};
  return {
    fullName: getFieldValue(form, 'fullName'),
    email: getFieldValue(form, 'email'),
    phone: getFieldValue(form, 'phone'),
    experience: getFieldValue(form, 'experience'),
    designation: getFieldValue(form, 'designation'),
    organisation: '',
    industry: getFieldValue(form, 'industry'),
    income: getFieldValue(form, 'income'),
    scope: getFieldValue(form, 'scope'),
    q7: getFieldValue(form, 'q7'),
    q8: getFieldValue(form, 'q8'),
    q9: getFieldValue(form, 'q9'),
  };
}

function snapshotBriefingForm(form) {
  if (!form) return {};
  return {
    fullName: getFieldValue(form, 'fullName'),
    email: getFieldValue(form, 'email'),
    phone: getFieldValue(form, 'phone'),
    city: getFieldValue(form, 'city'),
    designation: getFieldValue(form, 'designation'),
    industry: getFieldValue(form, 'industry'),
    experience: getFieldValue(form, 'wExperience'),
    income: getFieldValue(form, 'wIncome'),
    objectives: getFieldValue(form, 'objectives'),
    source: getFieldValue(form, 'wSource'),
  };
}

const testimonials = [
  {
    headline: '"I grew more in 3 years than I had in the previous 10."',
    body: 'I started stepping into conversations I used to hesitate to enter. I realized I had been operating below my potential — not because I lacked capability, but because I had never been trained to think at that level.',
    name: 'Sushma Patil',
    role: 'Principal Consultant, Infosys',
    avatar: '/images/100bm/testimonials/susma.png',
  },
  {
    headline: '"I took on 2x the responsibility and felt steady."',
    body: 'I took on 2x the responsibility and felt steady and confident through the transition. Building my profile changed how I positioned myself — it gave me greater clarity, stronger conviction, and a sharper sense of direction. It directly influenced the kind of roles I was considered for and the conversations I was invited into.',
    name: 'Anu Kannan',
    role: 'VP, Domain Architecture — Sustainability Technology, Deutsche Bank',
    avatar: '/images/100bm/testimonials/ANU.png',
  },
  {
    headline: '"Often the only woman in the room. The program changed that."',
    body: 'I needed more than competence. I needed strategic confidence and clarity under pressure. It changed how I think at senior levels and how I handle challenges.',
    name: 'Harvinder Kaur',
    role: 'Founder & CEO, Monaargy Consultancy',
    avatar: '/images/100bm/testimonials/harvinder.png',
  },
  {
    headline: '"Zero to ₹30 crore. One state to fourteen. In two years."',
    body: 'I joined at a turning point — moving from a senior corporate role into entrepreneurship. The shift was not just strategic, it was personal. I became clearer, more decisive, and far more confident in handling pressure. What stayed with me most was the mindset shift. It changed how I lead, how I respond, and how I grow.',
    name: 'Minal Bhagat',
    role: 'Co-Founder & Director, Ensonic Computech Pvt. Ltd.',
    avatar: '/images/100bm/testimonials/minal.png',
  },
];

const APPLICATION_THANK_YOU_URL = 'https://workshops.iamironlady.com/100-bm-Thankyou';

export default function BoardMembers100() {
  const [modalOpen, setModalOpen] = useState(false);
  const [webinarModalOpen, setWebinarModalOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [thankYouPage, setThankYouPage] = useState({ show: false }); // Executive Briefing only
  const [tIndex, setTIndex] = useState(0);
  const autoTimerRef = useRef(null);
  const revealRefs = useRef([]);

  const [appFormData, setAppFormData] = useState({});
  const [briefingFormData, setBriefingFormData] = useState({});
  const appSubmittedRef = useRef(false);
  const briefingSubmittedRef = useRef(false);
  const appFormDataRef = useRef({});
  const briefingFormDataRef = useRef({});
  const applicationFormRef = useRef(null);
  const applicationModalBoxRef = useRef(null);
  const [appFormStep, setAppFormStep] = useState(1);
  const briefingFormRef = useRef(null);
  const closingApplicationModalRef = useRef(false);
  const closingBriefingModalRef = useRef(false);
  const modalOpenRef = useRef(false);
  const webinarModalOpenRef = useRef(false);
  const applicationStartTrackedRef = useRef(false);

  const send100bmEvent = (eventName, params = {}) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, { page_location: window.location.href, ...params });
    }
  };

  const [lineHeight, setLineHeight] = useState(0);
  const [lastStepReached, setLastStepReached] = useState(false);
  const stepRefs = useRef([]);
  const stepsContainerRef = useRef(null);
  const heroSeatCtaRef = useRef(null);
  const finalCtaRef = useRef(null);
  const finalCtaSeatRef = useRef(null);
  const seatCtaMergedRef = useRef(false);
  const [heroSeatCtaInView, setHeroSeatCtaInView] = useState(true);
  /** float = fixed bar | merged = sticky in final section | hidden = past section / hero */
  const [seatCtaMode, setSeatCtaMode] = useState('hidden');
  const seatBarRafRef = useRef(null);
  const tTotal = testimonials.length;

  const SEAT_CTA_FLOAT_BOTTOM = 48;
  const SEAT_CTA_MERGE_HYSTERESIS = 40;

  const addRevealRef = useCallback((el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(Styles['reveal-active']);
          }
        });
      },
      { root: null, rootMargin: '0px 0px 50px 0px', threshold: 0.08 }
    );

    revealRefs.current.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const container = stepsContainerRef.current;
      const firstStep = stepRefs.current[0];
      const lastStep = stepRefs.current[3];
      if (!container || !firstStep || !lastStep) return;

      const containerRect = container.getBoundingClientRect();
      const firstStepRect = firstStep.getBoundingClientRect();
      const lastStepRect = lastStep.getBoundingClientRect();

      const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const circleCenter = w <= 360 ? 52 : w <= 768 ? 56 : 68;
      const lineStartOffset = (firstStepRect.top - containerRect.top) + circleCenter;
      const lineEndOffset = (lastStepRect.top - containerRect.top) + circleCenter;
      const trackHeight = lineEndOffset - lineStartOffset;
      if (trackHeight <= 0) return;

      const viewportTrigger = window.innerHeight * 0.6;
      const lineStartScreen = containerRect.top + lineStartOffset;

      if (lineStartScreen >= viewportTrigger) {
        setLineHeight(0);
        setLastStepReached(false);
        return;
      }

      const scrolled = viewportTrigger - lineStartScreen;
      const pixelHeight = Math.min(Math.max(scrolled, 0), trackHeight);
      setLineHeight(pixelHeight);
      setLastStepReached(pixelHeight >= trackHeight * 0.95);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const heroEl = heroSeatCtaRef.current;
    if (!heroEl) return;

    const obsHero = new IntersectionObserver(
      ([entry]) => setHeroSeatCtaInView(entry.isIntersecting),
      { root: null, threshold: 0, rootMargin: '0px' }
    );
    obsHero.observe(heroEl);

    return () => obsHero.disconnect();
  }, []);

  useEffect(() => {
    const updateSeatCtaMode = () => {
      const section = finalCtaRef.current;
      const anchor = finalCtaSeatRef.current;
      if (!section || !anchor) return;

      const sr = section.getBoundingClientRect();
      const ar = anchor.getBoundingClientRect();
      const vh = window.innerHeight;
      const mergeLine = vh - SEAT_CTA_FLOAT_BOTTOM;

      if (sr.bottom <= 0 || ar.bottom < 0) {
        seatCtaMergedRef.current = false;
        setSeatCtaMode('hidden');
        return;
      }

      if (seatCtaMergedRef.current) {
        if (ar.bottom > mergeLine + SEAT_CTA_MERGE_HYSTERESIS && ar.top > vh) {
          seatCtaMergedRef.current = false;
        }
      } else if (ar.bottom <= mergeLine) {
        seatCtaMergedRef.current = true;
      }

      setSeatCtaMode(seatCtaMergedRef.current ? 'merged' : 'float');
    };

    const scheduleSeatCtaMode = () => {
      if (seatBarRafRef.current != null) cancelAnimationFrame(seatBarRafRef.current);
      seatBarRafRef.current = requestAnimationFrame(() => {
        seatBarRafRef.current = null;
        updateSeatCtaMode();
      });
    };

    window.addEventListener('scroll', scheduleSeatCtaMode, { passive: true });
    window.addEventListener('resize', scheduleSeatCtaMode);
    scheduleSeatCtaMode();

    return () => {
      window.removeEventListener('scroll', scheduleSeatCtaMode);
      window.removeEventListener('resize', scheduleSeatCtaMode);
      if (seatBarRafRef.current != null) cancelAnimationFrame(seatBarRafRef.current);
    };
  }, []);

  const goNext = useCallback(() => {
    setTIndex((prev) => (prev + 1) % tTotal);
  }, [tTotal]);

  const startAuto = useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(goNext, 5500);
  }, [goNext]);

  const stopAuto = useCallback(() => {
    if (autoTimerRef.current) clearInterval(autoTimerRef.current);
  }, []);

  useEffect(() => {
    startAuto();
    return () => stopAuto();
  }, [startAuto, stopAuto]);

  const handlePrev = () => {
    setTIndex((prev) => (prev > 0 ? prev - 1 : prev));
    stopAuto();
    startAuto();
  };

  const handleNext = () => {
    setTIndex((prev) => (prev + 1) % tTotal);
    stopAuto();
    startAuto();
  };

  const handleDotClick = (i) => {
    setTIndex(i);
    stopAuto();
    startAuto();
  };

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
    stopAuto();
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && tIndex < tTotal - 1) {
        setTIndex((prev) => prev + 1);
      } else if (diff < 0 && tIndex > 0) {
        setTIndex((prev) => prev - 1);
      }
    }
    startAuto();
  };

  const trackField = (setter, ref) => (e) => {
    const { name, value } = e.target;
    setter((prev) => {
      const next = { ...prev, [name]: value };
      ref.current = next;
      return next;
    });
  };

  const openModal = () => {
    appSubmittedRef.current = false;
    applicationStartTrackedRef.current = false;
    setAppFormStep(1);
    setAppFormData({});
    appFormDataRef.current = {};
    setModalOpen(true);
    modalOpenRef.current = true;
    document.body.style.overflow = 'hidden';
    send100bmEvent('100bm_apply_click');
  };

  const closeModal = async () => {
    if (closingApplicationModalRef.current) return;
    closingApplicationModalRef.current = true;
    const snapshot = snapshotApplicationForm(applicationFormRef.current);
    appFormDataRef.current = snapshot;
    try {
      await syncPartialToApi('application', snapshot, appSubmittedRef);
    } finally {
      closingApplicationModalRef.current = false;
      setAppFormData({});
      appFormDataRef.current = {};
      setAppFormStep(1);
      setModalOpen(false);
      modalOpenRef.current = false;
      document.body.style.overflow = '';
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) void closeModal();
  };

  const openWebinarModal = () => {
    briefingSubmittedRef.current = false;
    setBriefingFormData({});
    briefingFormDataRef.current = {};
    setWebinarModalOpen(true);
    webinarModalOpenRef.current = true;
    document.body.style.overflow = 'hidden';
    send100bmEvent('100bm_webinar_click');
  };

  const closeWebinarModal = async () => {
    if (closingBriefingModalRef.current) return;
    closingBriefingModalRef.current = true;
    const snapshot = snapshotBriefingForm(briefingFormRef.current);
    briefingFormDataRef.current = snapshot;
    try {
      await syncPartialToApi('briefing', snapshot, briefingSubmittedRef);
    } finally {
      closingBriefingModalRef.current = false;
      setBriefingFormData({});
      briefingFormDataRef.current = {};
      setWebinarModalOpen(false);
      webinarModalOpenRef.current = false;
      document.body.style.overflow = '';
    }
  };

  const handleWebinarOverlayClick = (e) => {
    if (e.target === e.currentTarget) void closeWebinarModal();
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (modalOpen) void closeModal();
        if (webinarModalOpen) void closeWebinarModal();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [modalOpen, webinarModalOpen]);

  const trackAppField = trackField(setAppFormData, appFormDataRef);
  const trackBriefingField = trackField(setBriefingFormData, briefingFormDataRef);

  useEffect(() => {
    void flushPendingPartials();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flushPendingPartials();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (modalOpenRef.current && !appSubmittedRef.current) {
        const data = snapshotApplicationForm(applicationFormRef.current);
        if (formHasAnyValue(data)) {
          submitPartialBeacon('application', data);
        }
      }
      if (webinarModalOpenRef.current && !briefingSubmittedRef.current) {
        const data = snapshotBriefingForm(briefingFormRef.current);
        if (formHasAnyValue(data)) {
          submitPartialBeacon('briefing', data);
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: '', message: '' }), 5000);
  };

  const [appSubmitting, setAppSubmitting] = useState(false);

  const validateApplicationStep = (step) => {
    const form = applicationFormRef.current;
    if (!form) return false;
    const err = (msg) => {
      showToast('error', msg);
    };

    if (step === 1) {
      const fullName = getFieldValue(form, 'fullName');
      const email = getFieldValue(form, 'email');
      const phone = getFieldValue(form, 'phone');
      if (!fullName.trim()) {
        err('Please enter your full name.');
        form.elements.namedItem('fullName')?.focus?.();
        return false;
      }
      if (!email.trim()) {
        err('Please enter your email address.');
        form.elements.namedItem('email')?.focus?.();
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        err('Please enter a valid email address.');
        form.elements.namedItem('email')?.focus?.();
        return false;
      }
      if (!phone.trim()) {
        err('Please enter your phone number.');
        form.elements.namedItem('phone')?.focus?.();
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (!getFieldValue(form, 'experience')) {
        err('Please select your years of professional experience.');
        return false;
      }
      if (!getFieldValue(form, 'designation').trim()) {
        err('Please enter your current designation or title.');
        form.elements.namedItem('designation')?.focus?.();
        return false;
      }
      if (!getFieldValue(form, 'industry').trim()) {
        err('Please enter your current industry.');
        form.elements.namedItem('industry')?.focus?.();
        return false;
      }
      if (!getFieldValue(form, 'income')) {
        err('Please select your yearly income range.');
        return false;
      }
      if (!getFieldValue(form, 'scope')) {
        err('Please select your current scope of responsibility.');
        return false;
      }
      return true;
    }

    if (step === 3) {
      const q7 = getFieldValue(form, 'q7').trim();
      const q8 = getFieldValue(form, 'q8').trim();
      const q9 = getFieldValue(form, 'q9').trim();
      if (q7.length < 20) {
        err('Please complete question 6 (at least a few sentences).');
        form.elements.namedItem('q7')?.focus?.();
        return false;
      }
      if (q8.length < 20) {
        err('Please complete question 7 (at least a few sentences).');
        form.elements.namedItem('q8')?.focus?.();
        return false;
      }
      if (q9.length < 20) {
        err('Please complete question 8 (at least a few sentences).');
        form.elements.namedItem('q9')?.focus?.();
        return false;
      }
      return true;
    }

    return true;
  };

  const handleAppFormNext = () => {
    if (!validateApplicationStep(appFormStep)) return;
    const snapshot = snapshotApplicationForm(applicationFormRef.current);
    appFormDataRef.current = snapshot;
    setAppFormStep((s) => Math.min(3, s + 1));
  };

  const handleAppFormBack = () => {
    setAppFormStep((s) => Math.max(1, s - 1));
  };

  useEffect(() => {
    if (!modalOpen) return;
    const box = applicationModalBoxRef.current;
    if (box) box.scrollTo({ top: 0, behavior: 'smooth' });
  }, [appFormStep, modalOpen]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (appFormStep !== 3) return;
    const form = e.target;
    if (!validateApplicationStep(1)) {
      setAppFormStep(1);
      return;
    }
    if (!validateApplicationStep(2)) {
      setAppFormStep(2);
      return;
    }
    if (!validateApplicationStep(3)) {
      setAppFormStep(3);
      return;
    }
    setAppSubmitting(true);
    const formData = {
      fullName: form.fullName.value,
      email: form.email.value,
      phone: form.phone.value,
      experience: form.experience.value,
      designation: form.designation.value,
      organisation: '',
      industry: form.industry.value,
      income: form.income.value,
      scope: form.scope.value,
      q7: form.q7.value,
      q8: form.q8.value,
      q9: form.q9.value,
    };

    try {
      const apiFields = applicationSnapshotToFields(formData);
      let thankYouUrl = APPLICATION_THANK_YOU_URL;
      try {
        const resp = await submitFinalDraft({
          formType: 'application',
          fields: apiFields,
          step: 3,
        });
        if (resp?.thankyou) thankYouUrl = resp.thankyou;
      } catch (apiErr) {
        await submitToZoho('application', formData);
      }
      appSubmittedRef.current = true;
      send100bmEvent('100bm_application_submitted');
      setAppFormData({});
      appFormDataRef.current = {};
      form.reset();
      await closeModal();
      trackGoogleAdsSubmitLeadFormConversion();
      window.location.replace(thankYouUrl);
    } catch (err) {
      showToast('error', 'Something went wrong. Please try again.');
    } finally {
      setAppSubmitting(false);
    }
  };

  const [briefingSubmitting, setBriefingSubmitting] = useState(false);

  const handleWebinarFormSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    setBriefingSubmitting(true);
    const formData = {
      fullName: form.fullName.value,
      email: form.email.value,
      phone: form.phone.value,
      city: form.city.value,
      designation: form.designation.value,
      industry: form.industry.value,
      experience: form.wExperience.value,
      income: form.wIncome.value,
      objectives: form.objectives.value,
      source: form.wSource.value,
    };

    try {
      await submitToZoho('briefing', formData);
      briefingSubmittedRef.current = true;
      setBriefingFormData({});
      briefingFormDataRef.current = {};
      form.reset();
      await closeWebinarModal();
      trackGoogleAdsSubmitLeadFormConversion();
      setThankYouPage({ show: true });
    } catch (err) {
      showToast('error', 'Something went wrong. Please try again.');
    } finally {
      setBriefingSubmitting(false);
    }
  };

  useEffect(() => {
    if (window.location.hash === '#apply') openModal();
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const closeThankYouPage = () => {
    setThankYouPage({ show: false });
  };

  const seatCtaAvailable =
    !thankYouPage.show && !modalOpen && !webinarModalOpen;

  const seatCtaActive = seatCtaAvailable && !heroSeatCtaInView;
  const showFloatSeatCta = seatCtaActive && seatCtaMode === 'float';
  const showMergedSeatCta = seatCtaActive && seatCtaMode === 'merged';

  const seatCtaButton = (
    <button type="button" className={Styles['btn-primary']} onClick={openModal}>
      <span>Request Your Seat</span> →
    </button>
  );

  return (
    <div
      className={`${Styles['page-wrapper']} ${showFloatSeatCta ? Styles['page-wrapper--sticky-seat'] : ''}`}
    >
      <Helmet>
        <title>100 Board Member Program | Women Leaders for Boardroom Excellence</title>
        <meta name="description" content="Join the 100 Board Member Program to prepare for leadership at the highest levels. Empower yourself with the skills, strategies, and confidence to excel in boardroom roles." />
        <link rel="canonical" href="https://iamironlady.com/100-board-members" />
      </Helmet>

      {/* THANK YOU PAGE */}
      {thankYouPage.show && (
        <div className={Styles['thank-you-page']}>
          <div className={Styles['thank-you-backdrop']} onClick={closeThankYouPage} aria-hidden="true" />
          <div className={Styles['thank-you-box']}>
            <div className={Styles['thank-you-icon']}>✓</div>
            <h1 className={Styles['thank-you-title']}>Thank you for registering for the Executive Briefing.</h1>
            <p className={Styles['thank-you-text']}>This session will offer a closer look at the intent, structure, and experience of the 100 Board Members program. A confirmation email with the session details will reach you shortly.</p>
            <button type="button" className={Styles['thank-you-btn']} onClick={closeThankYouPage}>
              Back to 100 Board Members
            </button>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className={Styles.hero}>
        <div
          className={Styles['hero-bg-image']}
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ''}/background.png)` }}
          aria-hidden
        />
        <div className={Styles['hero-overlay']} />
        <div className={Styles['hero-inner']}>
          <div className={Styles['hero-content']}>
            <div className={Styles['hero-eyebrow']}>For women who lead</div>
            <div className={Styles['hero-brand']}>100 Board Members</div>
            <div className={Styles['hero-tagline']}>
              Stop climbing.
              <span>Start commanding.</span>
            </div>
            <p className={Styles['hero-sub']}>A cohort that fast-tracks your growth. Turn a 10-year climb into a 2-year leap.</p>
            <div className={Styles['hero-cta']} ref={heroSeatCtaRef}>
              <div className={Styles['hero-cta-group']}>
                <button className={Styles['btn-primary']} onClick={openModal}><span>Request Your Seat</span> →</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO SECTION */}
      <section className={Styles['video-section']}>
        <div className={Styles['video-wrap']}>
          <iframe
            src="https://www.youtube.com/embed/HPAP0eBHauk?autoplay=1&mute=1&loop=1&playlist=HPAP0eBHauk&rel=0&modestbranding=1&showinfo=0&controls=1"
            title="100 Board Members"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </section>

      {/* CLAIM YOUR AUTHORITY */}
      <section className={Styles.authority}>
        <div className={Styles['authority-grid']}>
          <div className={`${Styles['authority-left']} ${Styles.reveal}`} ref={addRevealRef}>
            <span className={Styles.eyebrow}>The turning point</span>
            <h2>
              Claim your{' '}
              <span className={Styles['blink-wrap']}>
                <span className={Styles['blink-word']}>authority.</span>
                <span className={Styles['blink-word']}>pay.</span>
                <span className={Styles['blink-word']}>seat.</span>
              </span>
            </h2>
            <p>Be the woman who refuses to be overlooked, play at the level you belong.</p>
            <p className={Styles['authority-coda']}>You have done the work. Now own the room.</p>
          </div>
          <div className={Styles['authority-right']}>
            <ul className={`${Styles['shift-list']} ${Styles.reveal}`} ref={addRevealRef}>
              <li><span className={Styles['shift-from']}>Ignored</span><div className={Styles['shift-row']}><span className={Styles['shift-arrow']}>→</span><span className={Styles['shift-to']}>Leading</span></div></li>
              <li><span className={Styles['shift-from']}>Invisible</span><div className={Styles['shift-row']}><span className={Styles['shift-arrow']}>→</span><span className={Styles['shift-to']}>Indispensable</span></div></li>
              <li><span className={Styles['shift-from']}>Stuck</span><div className={Styles['shift-row']}><span className={Styles['shift-arrow']}>→</span><span className={Styles['shift-to']}>Unstoppable</span></div></li>
              <li><span className={Styles['shift-from']}>Unheard</span><div className={Styles['shift-row']}><span className={Styles['shift-arrow']}>→</span><span className={Styles['shift-to']}>Undeniable</span></div></li>
            </ul>
          </div>
        </div>
      </section>

      {/* FEATURED IN */}
      <section className={`${Styles['featured-section']} ${Styles.reveal}`} ref={addRevealRef}>
        <span className={Styles.eyebrow}>Featured</span>
        <div className={Styles['featured-magazine']}>
          <div className={Styles['featured-magazine-inner']}>
            <img src="/2.png" alt="Iron Lady featured in Times of India" />
          </div>
        </div>
      </section>

      {/* YOUR BREAKTHROUGH */}
      <section className={Styles.breakthrough}>
        <div className={`${Styles['breakthrough-header']} ${Styles.reveal}`} ref={addRevealRef}>
          <span className={Styles.eyebrow}>What you're stepping into</span>
          <h2>Your breakthrough starts here.</h2>
          <p className={Styles.sub}>Know exactly what you're walking into. And what you're leaving behind.</p>
        </div>
        <div className={Styles['what-grid']}>
          {[
            { label: 'Authority by design', body: 'A deliberate structure for authority and influence.', not: 'Not casual networking disguised as progress' },
            { label: 'Top-level positioning', body: 'Engineered for top-level visibility and positioning.', not: 'Not visibility without strategy or substance' },
            { label: '3X ACCELERATION', body: 'Structured to help you multiply your career trajectory in a shorter span.', not: 'Not growth left to hope and timing' },
            { label: 'Compensation that matches capability', body: 'A shift from underpaid to compensation that reflects your true value.', not: 'Not staying undervalued while overdelivering' },
            { label: 'What Takes 10 Years → Built in 2', body: 'Turning a decade of career growth into two years of positioned authority', not: 'Not waiting years to be noticed organically' },
            { label: 'Inside the rooms that matter', body: 'Strategic access to decision-level rooms and relationships.', not: 'Not sitting outside while others decide' },
          ].map((card, i) => (
            <div key={i} className={`${Styles['what-card']} ${Styles.reveal}`} ref={addRevealRef}>
              <div className={Styles['what-label']}>{card.label}</div>
              <div className={Styles['what-body']}>{card.body}</div>
              <div className={Styles['what-not']}>{card.not}</div>
            </div>
          ))}
        </div>
      </section>

      {/* IS THIS YOU */}
      <section className={Styles['for-who']}>
        <div className={Styles['for-who-inner']}>
          <div className={`${Styles['for-who-header']} ${Styles.reveal}`} ref={addRevealRef}>
            <span className={Styles.eyebrow}>Eligibility</span>
            <h2>Is this you?</h2>
            <p className={Styles.intro}>You're doing more than your title reflects, and it's time that shifted.</p>
          </div>
          <div className={`${Styles['for-who-requirements']} ${Styles.reveal}`} ref={addRevealRef}>
            <div className={Styles['guardrail-sm']}>
              <span className={Styles['guardrail-sm-icon']}>◆</span>
              <span>Minimum 8 years of professional experience required</span>
            </div>
            <ul className={Styles.checklist}>
              <li><span className={Styles.check}>✦</span>You're targeting next-level leadership in the next 2–3 years, not just someday.</li>
              <li><span className={Styles.check}>✦</span>You want a meaningful compensation leap, not small increments.</li>
              <li><span className={Styles.check}>✦</span>You're navigating politics, perception gaps, or being underestimated.</li>
              <li><span className={Styles.check}>✦</span>You're ready to move from executor to decision-maker.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* INSIDE THE PROGRAM */}
      <section className={Styles.program} id="program-section">
        <div className={`${Styles['program-header']} ${Styles.reveal}`} ref={addRevealRef}>
          <span className={Styles.eyebrow}>Inside the program</span>
          <h2>How Women Claim Authority Collectively.</h2>
          <p>Everything within 100 Board Members is built to translate your experience into visible and legible authority.</p>
        </div>
        <div className={Styles['program-grid']}>
          <div className={`${Styles['prog-card']} ${Styles.reveal}`} ref={addRevealRef}>
            <div className={Styles['prog-icon']}>◉</div>
            <div>
              <div className={Styles['meth-title']}>Cohort-Based Learning</div>
              <div className={Styles['meth-body']}>Experiential sessions with a curated cohort of senior women. You learn alongside peers who match your ambition, not below it.</div>
            </div>
          </div>
          <div className={`${Styles['prog-card']} ${Styles.reveal}`} ref={addRevealRef}>
            <div className={Styles['prog-icon']}>◎</div>
            <div>
              <div className={Styles['meth-title']}>Career Politics, Decoded</div>
              <div className={Styles['meth-body']}>Politics, bias, and the moments no one prepares you for — brought into the room and worked through in real time, through structured activities.</div>
            </div>
          </div>
        </div>
        <div className={Styles['program-grid-3']}>
          <div className={`${Styles['prog-card']} ${Styles.reveal}`} ref={addRevealRef}>
            <div className={Styles['prog-icon']}>◈</div>
            <div>
              <div className={Styles['meth-title']}>Access to Top Leaders</div>
              <div className={Styles['meth-body']}>Sessions with senior members and investors who have held the rooms you're targeting.</div>
            </div>
          </div>
          <div className={`${Styles['prog-card']} ${Styles.reveal}`} ref={addRevealRef}>
            <div className={Styles['prog-icon']}>◇</div>
            <div>
              <div className={Styles['meth-title']}>Confidential Intensive Sessions</div>
              <div className={Styles['meth-body']}>Closed-room, online sessions to work through your personal, high-stakes challenges in a private space.</div>
            </div>
          </div>
          <div className={`${Styles['prog-card']} ${Styles.reveal}`} ref={addRevealRef}>
            <div className={Styles['prog-icon']}>⬡</div>
            <div>
              <div className={Styles['meth-title']}>Practice and Mastery Huddles</div>
              <div className={Styles['meth-body']}>Ongoing activity sessions to sharpen the capabilities and tactics covered across the program.</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className={Styles.testimonials} id="testimonials-section">
        <div className={`${Styles['testimonials-header']} ${Styles.reveal}`} ref={addRevealRef}>
          <span className={Styles.eyebrow}>Real results. Real women.</span>
          <h2>In their words.</h2>
        </div>
        <div
          className={Styles['t-slideshow-wrap']}
          onMouseEnter={stopAuto}
          onMouseLeave={startAuto}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button className={`${Styles['t-slide-btn']} ${Styles['t-prev']}`} onClick={handlePrev} disabled={tIndex === 0} aria-label="Previous">←</button>
          <div className={Styles['t-slideshow']}>
            <div className={Styles['t-track']} style={{ transform: `translateX(-${tIndex * 100}%)` }}>
              {testimonials.map((t, i) => (
                <div key={i} className={`${Styles['t-card']} ${Styles['t-slide']}`}>
                  <div className={Styles['t-headline']}>{t.headline}</div>
                  <p className={Styles['t-body']}>{t.body}</p>
                  <div className={Styles['t-author']}>
                    <img className={Styles['t-avatar']} src={t.avatar} alt={t.name} />
                    <div>
                      <div className={Styles['t-name']}>{t.name}</div>
                      <div className={Styles['t-role']}>{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button className={`${Styles['t-slide-btn']} ${Styles['t-next']}`} onClick={handleNext} aria-label="Next">→</button>
        </div>
        <div className={Styles['t-dots']}>
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`${Styles['t-dot']} ${i === tIndex ? Styles['t-dot-active'] : ''}`}
              onClick={() => handleDotClick(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ENTRY */}
      <section className={Styles.entry}>
        <div className={Styles['entry-top']}>
          <span className={Styles.eyebrow}>Entry</span>
          <h2>Entry into 100 Board Members is selective by design.</h2>
          <p className={Styles['entry-note']}>Not everyone who applies is selected.</p>
        </div>
        <div className={Styles.steps} ref={stepsContainerRef}>
          <div
            className={Styles['steps-progress']}
            style={{ height: `${lineHeight}px` }}
          />
          {[
            { num: '01', title: 'Application', body: 'Complete the detailed selection form outlining your experience, scope of impact, and next-level intent.' },
            { num: '02', title: 'Review', body: 'Selection is based on substance, demonstrated experience, and readiness.' },
            { num: '03', title: 'Confirmation', body: 'Applications are reviewed based on readiness and experience. Selected candidates receive formal confirmation and enrolment details.' },
            { num: '04', title: 'Enrolment', body: 'Complete your enrolment to join the cohort and begin your journey as part of the 100 Board Members program.' },
          ].map((step, i) => (
            <div
              key={i}
              className={`${Styles.step} ${Styles.reveal}`}
              ref={(el) => {
                addRevealRef(el);
                if (el) stepRefs.current[i] = el;
              }}
            >
              <span className={`${Styles['step-num']} ${i === 3 && lastStepReached ? Styles['step-completed'] : ''}`}>
                {i === 3 && lastStepReached ? '✓' : step.num}
              </span>
              <div className={Styles['step-content']}>
                <div className={Styles['step-title']}>{step.title}</div>
                <div className={Styles['step-body']}>{step.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className={Styles['final-cta']} id="apply-section" ref={finalCtaRef}>
        <div className={Styles['final-cta-content']}>
          <span className={Styles['cta-eyebrow']}>The moment is now</span>
          <h2>Do you believe you are ready for the seat?</h2>
          <p className={Styles['cta-sub']}>Then don't wait for someone to hand you the next level.</p>
        </div>
        <div
          ref={finalCtaSeatRef}
          className={`${Styles['final-cta-seat']} ${showMergedSeatCta ? Styles['final-cta-seat--active'] : ''}`}
          role="region"
          aria-label="Apply"
          aria-hidden={!showMergedSeatCta}
        >
          {seatCtaButton}
        </div>
      </section>

      {showFloatSeatCta && (
        <div className={Styles['sticky-seat-bar']} role="region" aria-label="Apply">
          <div className={Styles['sticky-seat-bar-inner']}>
            {seatCtaButton}
          </div>
        </div>
      )}

      {/* APPLICATION MODAL */}
      <div
        className={`${Styles['modal-overlay']} ${modalOpen ? Styles['modal-open'] : ''}`}
        onClick={handleOverlayClick}
        aria-hidden={!modalOpen}
      >
        <div className={Styles['modal-box']} ref={applicationModalBoxRef}>
          <button type="button" className={Styles['modal-close']} onClick={() => void closeModal()} aria-label="Close">×</button>
          <h2 className={Styles['modal-title']}>100 Board Members</h2>
          <p className={Styles['modal-subtitle']}>Apply to the 100 Board Members Program</p>
          <div className={Styles['modal-intro']}>
            <p>100 Board Members is not open access.</p>
            <p>This application evaluates whether you are ready to move from capability to command. We review leadership maturity, scope of influence, and your intent to step into larger decision-making roles within the next 2–3 years.</p>
          </div>
          <p className={Styles['modal-step-indicator']} aria-live="polite">
            Step {appFormStep} of 3
          </p>
          <form
            ref={applicationFormRef}
            onSubmit={handleFormSubmit}
            onChange={trackAppField}
            onInput={trackAppField}
            className={Styles['modal-form']}
            noValidate
          >
            {appFormStep === 3 && (
              <div className={Styles['modal-form-summary']}>
                <p className={Styles['modal-form-summary-title']}>How we use your responses</p>
                <p className={Styles['modal-form-summary-text']}>
                  From this point onward, your answers are read in full so we can understand your leadership context, intent, and challenges. They inform how we assess fit and how we shape{' '}
                  <strong>more personalised development</strong> if you are invited to move ahead.
                </p>
                <p className={Styles['modal-form-summary-text']}>
                  Be specific about role, authority, and scope—not only title or compensation. As a guide, aim for roughly 150–200 words on questions 6 and 7, and about 200 words on question 8.
                </p>
              </div>
            )}

            <div className={Styles['modal-section']} data-application-step="1" hidden={appFormStep !== 1}>
              <div className={Styles['modal-field']}>
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  placeholder="Your full name"
                  autoComplete="name"
                  onFocus={() => {
                    if (!applicationStartTrackedRef.current) {
                      applicationStartTrackedRef.current = true;
                      send100bmEvent('100bm_application_start');
                    }
                  }}
                />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="email">Email Address *</label>
                <input type="email" id="email" name="email" placeholder="your@email.com" autoComplete="email" />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="phone">Phone Number *</label>
                <input type="tel" id="phone" name="phone" placeholder="+91 98765 43210" autoComplete="tel" />
              </div>
            </div>

            <div className={Styles['modal-section']} data-application-step="2" hidden={appFormStep !== 2}>
              <div className={Styles['modal-field']}>
                <label>1. Total years of full-time professional experience *</label>
                <div className={Styles['modal-radio-group']}>
                  <label><input type="radio" name="experience" value="8-10" /> 8–10 years</label>
                  <label><input type="radio" name="experience" value="11-15" /> 11–15 years</label>
                  <label><input type="radio" name="experience" value="15+" /> 15+ years</label>
                </div>
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="designation">2. Current designation / title *</label>
                <input type="text" id="designation" name="designation" placeholder="e.g. Senior Manager, Director" />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="industry">3. Current industry *</label>
                <input type="text" id="industry" name="industry" placeholder="e.g. Technology, Finance" />
              </div>
              <div className={Styles['modal-field']}>
                <label>4. Yearly income range *</label>
                <div className={Styles['modal-radio-group']}>
                  <label><input type="radio" name="income" value="Above 1Cr" /> Above ₹1 Cr</label>
                  <label><input type="radio" name="income" value="50L - 1Cr" /> ₹50L – ₹1 Cr</label>
                  <label><input type="radio" name="income" value="30L - 50L" /> ₹30L – ₹50L</label>
                  <label><input type="radio" name="income" value="20L - 30L" /> ₹20L – ₹30L</label>
                  <label><input type="radio" name="income" value="10L - 20L" /> ₹10L – ₹20L</label>
                  <label><input type="radio" name="income" value="Less than 10L" /> Less than ₹10 LPA</label>
                </div>
              </div>
              <div className={Styles['modal-field']}>
                <label>5. Current scope of responsibility *</label>
                <div className={Styles['modal-radio-group']}>
                  <label><input type="radio" name="scope" value="individual" /> Individual contributor</label>
                  <label><input type="radio" name="scope" value="small-team" /> Managing a small team (1–10 members)</label>
                  <label><input type="radio" name="scope" value="team-function" /> Leading a team, function, or department</label>
                  <label><input type="radio" name="scope" value="cross-functional" /> Cross-functional leadership responsibility</label>
                  <label><input type="radio" name="scope" value="pnl" /> Direct P&L / revenue / cost accountability</label>
                  <label><input type="radio" name="scope" value="others" /> Others</label>
                </div>
              </div>
            </div>

            <div className={Styles['modal-section']} data-application-step="3" hidden={appFormStep !== 3}>
              <div className={Styles['modal-field']}>
                <label htmlFor="q7">6. Why are you applying to 100 Board Members at this point in your career? *</label>
                <textarea id="q7" name="q7" placeholder="Share your story..." rows={6} />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="q8">7. Where do you want to see yourself in the next 3 years? *</label>
                <textarea id="q8" name="q8" placeholder="Describe your vision..." rows={6} />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="q9">8. What are the top challenges you go through as a leader today? *</label>
                <textarea id="q9" name="q9" placeholder="Describe your challenges..." rows={6} />
              </div>
            </div>

            <div className={Styles['modal-form-actions']}>
              {appFormStep > 1 && (
                <button type="button" className={Styles['modal-back-btn']} onClick={handleAppFormBack}>
                  Back
                </button>
              )}
              {appFormStep < 3 ? (
                <button type="button" className={Styles['modal-submit']} onClick={handleAppFormNext}>
                  Next
                </button>
              ) : (
                <button type="submit" className={Styles['modal-submit']} disabled={appSubmitting}>
                  {appSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* TOAST NOTIFICATION */}
      <div className={`${Styles['toast']} ${toast.show ? Styles['toast-show'] : ''} ${toast.type === 'error' ? Styles['toast-error'] : ''}`}>
        <span className={Styles['toast-icon']}>{toast.type === 'success' ? '✓' : '!'}</span>
        <span className={Styles['toast-msg']}>{toast.message}</span>
        <button className={Styles['toast-close']} onClick={() => setToast({ show: false, type: '', message: '' })}>×</button>
      </div>

      {/* WEBINAR REGISTRATION MODAL */}
      <div
        className={`${Styles['modal-overlay']} ${webinarModalOpen ? Styles['modal-open'] : ''}`}
        onClick={handleWebinarOverlayClick}
        aria-hidden={!webinarModalOpen}
      >
        <div className={Styles['modal-box']}>
          <button type="button" className={Styles['modal-close']} onClick={() => void closeWebinarModal()} aria-label="Close">×</button>
          <h2 className={Styles['modal-title']}>Executive Briefing</h2>
          <p className={Styles['modal-subtitle']}>Register for the 100 Board Members Briefing</p>
          <div className={Styles['modal-intro']}>
            <p>Executive Briefings are closed-door sessions where we explore how enterprise authority is built and evaluated. You will get a clearer picture of why senior careers plateau, how judgement is assessed at the top, and what 100 Board Members expects from its participants.</p>
            <p>These are assessment conversations, not sales sessions. Registering does not guarantee a place or eligibility for the program.</p>
          </div>
          <form
            ref={briefingFormRef}
            onSubmit={handleWebinarFormSubmit}
            onChange={trackBriefingField}
            onInput={trackBriefingField}
          >
            <div className={Styles['modal-section']}>
              <div className={Styles['modal-field']}>
                <label htmlFor="wFullName">Full Name *</label>
                <input type="text" id="wFullName" name="fullName" placeholder="Your full name" required autoComplete="name" />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="wEmail">Email *</label>
                <input type="email" id="wEmail" name="email" placeholder="your@email.com" required autoComplete="email" />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="wPhone">Phone Number *</label>
                <input type="tel" id="wPhone" name="phone" placeholder="+91 98765 43210" required autoComplete="tel" />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="wCity">City *</label>
                <input type="text" id="wCity" name="city" placeholder="e.g. Mumbai, Bangalore" required />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="wDesignation">Current Designation / Title *</label>
                <input type="text" id="wDesignation" name="designation" placeholder="e.g. Senior Manager, Director" required />
              </div>
              <div className={Styles['modal-field']}>
                <label htmlFor="wIndustry">Current Industry *</label>
                <input type="text" id="wIndustry" name="industry" placeholder="e.g. Technology, Finance" required />
              </div>
            </div>

            <div className={Styles['modal-section']}>
              <div className={Styles['modal-field']}>
                <label>Years of Experience *</label>
                <p className={Styles.hint}>Note: Applicants with fewer than 8 years of experience are not eligible.</p>
                <div className={Styles['modal-radio-group']}>
                  <label><input type="radio" name="wExperience" value="8-10" required /> 8–10 years</label>
                  <label><input type="radio" name="wExperience" value="11-15" /> 11–15 years</label>
                  <label><input type="radio" name="wExperience" value="15+" /> 15+ years</label>
                </div>
              </div>
              <div className={Styles['modal-field']}>
                <label>Income Range *</label>
                <div className={Styles['modal-radio-group']}>
                  <label><input type="radio" name="wIncome" value="Above 1Cr" required /> Above ₹1 Cr</label>
                  <label><input type="radio" name="wIncome" value="50L - 1Cr" /> ₹50L – ₹1 Cr</label>
                  <label><input type="radio" name="wIncome" value="30L - 50L" /> ₹30L – ₹50L</label>
                  <label><input type="radio" name="wIncome" value="20L - 30L" /> ₹20L – ₹30L</label>
                  <label><input type="radio" name="wIncome" value="10L - 20L" /> ₹10L – ₹20L</label>
                  <label><input type="radio" name="wIncome" value="Less than 10L" /> Less than ₹10 LPA</label>
                </div>
              </div>
            </div>

            <div className={Styles['modal-section']}>
              <div className={Styles['modal-field']}>
                <label htmlFor="wObjectives">What are the key objectives you aim to achieve by attending this webinar? *</label>
                <textarea id="wObjectives" name="objectives" placeholder="Share your objectives..." required minLength={20} />
              </div>
              <div className={Styles['modal-field']}>
                <label>How did you hear about 100 Board Members? *</label>
                <div className={Styles['modal-radio-group']}>
                  <label><input type="radio" name="wSource" value="instagram-linkedin" required /> Instagram / LinkedIn</label>
                  <label><input type="radio" name="wSource" value="referral" /> Referred by someone I know</label>
                  <label><input type="radio" name="wSource" value="google-search" /> Google / search</label>
                  <label><input type="radio" name="wSource" value="community" /> A community or group I'm part of</label>
                  <label><input type="radio" name="wSource" value="other" /> Other</label>
                </div>
              </div>
            </div>

            <button type="submit" className={Styles['modal-submit']} disabled={briefingSubmitting}>
              {briefingSubmitting ? 'Submitting...' : 'Register for Briefing'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
